import { ResponsiveBar } from "@nivo/bar";
import { startOfToday } from "date-fns";
import { defineMessages, useIntl } from "react-intl";

import { Heading } from "common/core/typography";

import { ChartsSection } from "./shared/charts_section";
import { EmptyChart } from "./shared/empty_chart";

const MESSAGES = defineMessages({
  chartsSectionHeading: {
    id: "8cf32e47-fd96-469b-bd51-add376b31e16",
    defaultMessage: "Sent vs. completed transactions",
  },
  sentChartTitle: {
    id: "ccd23d20-c129-47b0-8c9d-020a44054a87",
    defaultMessage: "Sent Transactions (Monthly)",
  },
  completedChartTitle: {
    id: "f39bcef9-2f21-4bcf-bd6d-3943bdd6beaa",
    defaultMessage: "Completed Transactions (Monthly)",
  },
  viewing6Months: {
    id: "025f6092-4804-4dd2-a526-e02cadf64701",
    defaultMessage: "Viewing {startDate} - {endDate} | Last 6 months",
  },
  sentTransactions: {
    id: "0f6c1a34-15a6-4c58-b498-492dc71c85a7",
    defaultMessage: "sent transactions",
  },
  completedTransactions: {
    id: "1b629c11-2e38-4d3a-9be7-dfcc88e694f5",
    defaultMessage: "completed transactions",
  },
  sentAt: {
    id: "691d5a2c-314a-406c-9e13-82372455e451",
    defaultMessage: "sent at",
  },
  completedAt: {
    id: "d543d46a-a0e9-49c5-9060-61d2066f726a",
    defaultMessage: "completed at",
  },
  emptySentChartTitle: {
    id: "bff3141e-3f15-400a-bc5e-dfb2b266c6f4",
    defaultMessage: "No sent transactions",
  },
  emptySentChartDescription: {
    id: "56e1eaf8-e8cc-439e-b86f-ecbbef7dc190",
    defaultMessage: "Monthly sent transactions will display here.",
  },
  emptyCompletedChartTitle: {
    id: "73e645df-f8f5-4015-8a23-2c8146831ccf",
    defaultMessage: "No completed transactions",
  },
  emptyCompletedChartDescription: {
    id: "2b79df11-accc-4b0d-b27c-5fecdb3f1d2c",
    defaultMessage: "Monthly completed transactions will display here.",
  },
});

const margin = { top: 25, right: 10, bottom: 75, left: 25 };
const axisBottomSettings = {
  legendOffset: 32,
};
const axisLeftSettings = {
  legendOffset: -20,
  format: () => "",
};
const sharedProps = {
  enableLabel: false,
  enableTotals: true,
  indexBy: "label",
  margin,
};

export function SentCompletedSection({
  chartData,
  sixMonthsStartDate,
  sixMonthsEndDate,
}: {
  chartData: NotarizeScalarHash[];
  sixMonthsStartDate: string | null;
  sixMonthsEndDate: string | null;
}) {
  const intl = useIntl();

  const viewing6Months = intl.formatMessage(MESSAGES.viewing6Months, {
    startDate: sixMonthsStartDate
      ? new Date(sixMonthsStartDate).toLocaleDateString("default", {
          month: "short",
          year: "numeric",
        })
      : "",
    endDate: sixMonthsEndDate
      ? new Date(sixMonthsEndDate).toLocaleDateString("default", {
          month: "short",
          year: "numeric",
        })
      : "",
  });

  const last6Months = Array.from({ length: 6 }).map((_, i) => {
    const d = new Date(startOfToday().getFullYear(), startOfToday().getMonth() - (5 - i), 1);
    return { month: d.getMonth(), year: d.getFullYear() };
  });

  const safeChartData = last6Months
    .map(({ month, year }) => {
      const match = chartData.find((d) => {
        const date = new Date(d.label as string);
        return date.getUTCFullYear() === year && date.getUTCMonth() === month;
      });
      return {
        label: new Date(year, month, 1).toLocaleString(intl.locale, { month: "short" }),
        sent: match?.sent_count ? Number(match.sent_count) : 0,
        completed: match?.completed_count ? Number(match.completed_count) : 0,
      };
    })
    .slice(-6);

  const maxSentValue = Math.max(...safeChartData.map((d) => d.sent), 0);
  const maxCompletedValue = Math.max(...safeChartData.map((d) => d.completed), 0);

  const numTicks = 10;
  // Ensure at least 1 tick mark even if max value is 1 or less
  const sentValuesToShow =
    maxSentValue <= 1
      ? [0, 1]
      : Array.from({ length: numTicks + 1 }, (_, i) => Math.ceil((maxSentValue * i) / numTicks));

  const completedValuesToShow =
    maxCompletedValue <= 1
      ? [0, 1]
      : Array.from({ length: numTicks + 1 }, (_, i) =>
          Math.ceil((maxCompletedValue * i) / numTicks),
        );

  return (
    <ChartsSection
      heading={intl.formatMessage(MESSAGES.chartsSectionHeading)}
      description={viewing6Months}
      firstChart={
        <>
          {maxSentValue <= 0 ? (
            <EmptyChart
              heading={intl.formatMessage(MESSAGES.emptySentChartTitle)}
              description={intl.formatMessage(MESSAGES.emptySentChartDescription)}
            />
          ) : (
            <>
              <Heading level="h3" textStyle="allCapsLabelSmall">
                {intl.formatMessage(MESSAGES.sentChartTitle)}
              </Heading>
              <ResponsiveBar
                data={safeChartData}
                keys={["sent"]}
                axisBottom={{
                  legend: intl.formatMessage(MESSAGES.sentAt),
                  legendPosition: "middle",
                  ...axisBottomSettings,
                }}
                gridYValues={sentValuesToShow}
                axisLeft={{
                  legend: intl.formatMessage(MESSAGES.sentTransactions),
                  tickValues: sentValuesToShow,
                  ...axisLeftSettings,
                }}
                colors="var(--primary-50)"
                {...sharedProps}
              />
            </>
          )}
        </>
      }
      secondChart={
        maxCompletedValue <= 0 ? (
          <EmptyChart
            heading={intl.formatMessage(MESSAGES.emptyCompletedChartTitle)}
            description={intl.formatMessage(MESSAGES.emptyCompletedChartDescription)}
          />
        ) : (
          <>
            <Heading level="h3" textStyle="allCapsLabelSmall">
              {intl.formatMessage(MESSAGES.completedChartTitle)}
            </Heading>
            <ResponsiveBar
              data={safeChartData}
              keys={["completed"]}
              axisBottom={{
                legend: intl.formatMessage(MESSAGES.completedAt),
                legendPosition: "middle",
                ...axisBottomSettings,
              }}
              gridYValues={completedValuesToShow}
              axisLeft={{
                legend: intl.formatMessage(MESSAGES.completedTransactions),
                tickValues: completedValuesToShow,
                ...axisLeftSettings,
              }}
              colors="var(--success-80)"
              {...sharedProps}
            />
          </>
        )
      }
    />
  );
}
